import { action } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface IAgtDigitalReportModel {
	vtnrId: number;
	bm: number;

	// All customers segment
	anzKnd: number;
	anzDigitalId: number;
	antDigitalId: number;
	anzMaz: number;
	antMaz: number;
	anzWewe: number;
	antWewe: number;

	// Age 18-70 segment
	anzKnd1870: number;
	anzDigitalId1870: number;
	antDigitalId1870: number;
	anzMaz1870: number;
	antMaz1870: number;
	anzWewe1870: number;
	antWewe1870: number;

	// New generation segment
	anzKndNg: number;
	anzDigitalIdNg: number;
	antDigitalIdNg: number;
	anzMazNg: number;
	antMazNg: number;
	anzWeweNg: number;
	antWeweNg: number;
}

export class AgtDigitalReportModel implements IAgtDigitalReportModel {
	vtnrId: number;
	bm: number;

	// All customers segment
	anzKnd: number;
	anzDigitalId: number;
	antDigitalId: number;
	anzMaz: number;
	antMaz: number;
	anzWewe: number;
	antWewe: number;

	// Age 18-70 segment
	anzKnd1870: number;
	anzDigitalId1870: number;
	antDigitalId1870: number;
	anzMaz1870: number;
	antMaz1870: number;
	anzWewe1870: number;
	antWewe1870: number;

	// New generation segment
	anzKndNg: number;
	anzDigitalIdNg: number;
	antDigitalIdNg: number;
	anzMazNg: number;
	antMazNg: number;
	anzWeweNg: number;
	antWeweNg: number;

	// Legacy compatibility properties
	agtId: number;

	// Legacy properties for backwards compatibility with old DigitalreportModel
	anzKunden: number;
	prozDigiId: number;
	prozMaz: number;
	prozWewe: number;
	anzKunden18: number;
	anzDigitalId18: number;
	prozDigiId18: number;
	anzMaz18: number;
	prozMaz18: number;
	anzWewe18: number;
	prozWewe18: number;
	anzKundenNeu: number;
	anzDigitalIdNeu: number;
	prozDigiIdNeu: number;
	anzMazNeu: number;
	prozMazNeu: number;
	anzWeweNeu: number;
	prozWeweNeu: number;

	constructor(data: any) {
		this.vtnrId = data.aid || 0;
		this.bm = data.bm ? parseInt(data.bm) : 0;

		// All customers segment - use short aliases
		this.anzKnd = data.k || 0;
		this.anzDigitalId = data.d || 0;
		this.antDigitalId = data.p ? data.p * 100 : 0;
		this.anzMaz = data.m || 0;
		this.antMaz = data.q ? data.q * 100 : 0;
		this.anzWewe = data.w || 0;
		this.antWewe = data.r ? data.r * 100 : 0;

		// Age 18-70 segment - use short aliases
		this.anzKnd1870 = data.k1 || 0;
		this.anzDigitalId1870 = data.d1 || 0;
		this.antDigitalId1870 = data.p1 ? data.p1 * 100 : 0;
		this.anzMaz1870 = data.m1 || 0;
		this.antMaz1870 = data.q1 ? data.q1 * 100 : 0;
		this.anzWewe1870 = data.w1 || 0;
		this.antWewe1870 = data.r1 ? data.r1 * 100 : 0;

		// New generation segment - use short aliases
		this.anzKndNg = data.k2 || 0;
		this.anzDigitalIdNg = data.d2 || 0;
		this.antDigitalIdNg = data.p2 ? data.p2 * 100 : 0;
		this.anzMazNg = data.m2 || 0;
		this.antMazNg = data.q2 ? data.q2 * 100 : 0;
		this.anzWeweNg = data.w2 || 0;
		this.antWeweNg = data.r2 ? data.r2 * 100 : 0;

		// Legacy compatibility - alias properties
		this.agtId = this.vtnrId;

		// Map to old property names for backwards compatibility
		this.anzKunden = this.anzKnd;
		this.prozDigiId = this.antDigitalId;
		this.prozMaz = this.antMaz;
		this.prozWewe = this.antWewe;

		this.anzKunden18 = this.anzKnd1870;
		this.anzDigitalId18 = this.anzDigitalId1870;
		this.prozDigiId18 = this.antDigitalId1870;
		this.anzMaz18 = this.anzMaz1870;
		this.prozMaz18 = this.antMaz1870;
		this.anzWewe18 = this.anzWewe1870;
		this.prozWewe18 = this.antWewe1870;

		this.anzKundenNeu = this.anzKndNg;
		this.anzDigitalIdNeu = this.anzDigitalIdNg;
		this.prozDigiIdNeu = this.antDigitalIdNg;
		this.anzMazNeu = this.anzMazNg;
		this.prozMazNeu = this.antMazNg;
		this.anzWeweNeu = this.anzWeweNg;
		this.prozWeweNeu = this.antWeweNg;
	}
}

export class AgtDigitalReportStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_digitalreport',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid' },
				{ column: 'bm', aggregateFunction: AggregateFunction.NONE },
				// All customers segment
				{ column: 'anz_knd', aggregateFunction: AggregateFunction.NONE, alias: 'k' },
				{ column: 'anz_digital_id', aggregateFunction: AggregateFunction.NONE, alias: 'd' },
				{ column: 'ant_digital_id', aggregateFunction: AggregateFunction.NONE, alias: 'p' },
				{ column: 'anz_maz', aggregateFunction: AggregateFunction.NONE, alias: 'm' },
				{ column: 'ant_maz', aggregateFunction: AggregateFunction.NONE, alias: 'q' },
				{ column: 'anz_wewe', aggregateFunction: AggregateFunction.NONE, alias: 'w' },
				{ column: 'ant_wewe', aggregateFunction: AggregateFunction.NONE, alias: 'r' },
				// Age 18-70 segment
				{ column: 'anz_knd_18_70', aggregateFunction: AggregateFunction.NONE, alias: 'k1' },
				{ column: 'anz_digital_id_18_70', aggregateFunction: AggregateFunction.NONE, alias: 'd1' },
				{ column: 'ant_digital_id_18_70', aggregateFunction: AggregateFunction.NONE, alias: 'p1' },
				{ column: 'anz_maz_18_70', aggregateFunction: AggregateFunction.NONE, alias: 'm1' },
				{ column: 'ant_maz_18_70', aggregateFunction: AggregateFunction.NONE, alias: 'q1' },
				{ column: 'anz_wewe_18_70', aggregateFunction: AggregateFunction.NONE, alias: 'w1' },
				{ column: 'ant_wewe_18_70', aggregateFunction: AggregateFunction.NONE, alias: 'r1' },
				// New generation segment
				{ column: 'anz_knd_ng', aggregateFunction: AggregateFunction.NONE, alias: 'k2' },
				{ column: 'anz_digital_id_ng', aggregateFunction: AggregateFunction.NONE, alias: 'd2' },
				{ column: 'ant_digital_id_ng', aggregateFunction: AggregateFunction.NONE, alias: 'p2' },
				{ column: 'anz_maz_ng', aggregateFunction: AggregateFunction.NONE, alias: 'm2' },
				{ column: 'ant_maz_ng', aggregateFunction: AggregateFunction.NONE, alias: 'q2' },
				{ column: 'anz_wewe_ng', aggregateFunction: AggregateFunction.NONE, alias: 'w2' },
				{ column: 'ant_wewe_ng', aggregateFunction: AggregateFunction.NONE, alias: 'r2' },
			],
		});
		this.queryName = '?digitalReport';
	}

	async getDistinctBMs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, distinct: true },
		];
		const results = await this.query(selectColumns);
		return results.map(data => parseInt(data.bm)).sort((a, b) => a - b);
	}

	@action
	async loadByBm(bm: number): Promise<AgtDigitalReportModel[]> {
		let whereClause = 'bm = ' + bm

		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map(data => new AgtDigitalReportModel(data));
	}

	@action
	async findAll(): Promise<AgtDigitalReportModel[]> {
		const results = await this.query(this.config.defaultColumns);
		return results.map(data => new AgtDigitalReportModel(data));
	}

	async findByAgtId(agtId: number): Promise<AgtDigitalReportModel | undefined> {
		const whereClause = 'a.vtnr_id = ' + agtId;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.length > 0 ? new AgtDigitalReportModel(results[0]) : undefined;
	}
}